import type { ComponentChildren } from "preact";
import { parseLinkMarker, STRINGS } from "../constants/strings";

interface FieldWrapProps {
  label: string;
  optional?: boolean;
  required?: boolean;
  error?: string;
  children: ComponentChildren;
}

function FieldWrap({
  label,
  optional,
  required,
  error,
  children,
}: FieldWrapProps) {
  return (
    <label class="tl-field">
      <span class="tl-field__label">
        <span>{label}</span>
        {optional && (
          <span class="tl-field__optional">{STRINGS.optionalMarker}</span>
        )}
        {required && <span class="tl-field__required">*</span>}
      </span>
      {children}
      {error && <span class="tl-field__error">{error}</span>}
    </label>
  );
}

interface TextInputProps {
  label: string;
  value: string;
  placeholder?: string;
  type?: string;
  required?: boolean;
  optional?: boolean;
  error?: string;
  maxLength?: number;
  onInput: (value: string) => void;
  onBlur?: () => void;
}

export function TextInput({
  label,
  value,
  placeholder,
  type = "text",
  required,
  optional,
  error,
  maxLength,
  onInput,
  onBlur,
}: TextInputProps) {
  const cls = "tl-input" + (error ? " tl-input--error" : "");
  return (
    <FieldWrap
      label={label}
      optional={optional}
      required={required}
      error={error}
    >
      <input
        class={cls}
        type={type}
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        onInput={(e) =>
          onInput((e.currentTarget as HTMLInputElement).value)
        }
        onBlur={onBlur}
      />
    </FieldWrap>
  );
}

interface TextareaProps {
  label: string;
  value: string;
  placeholder?: string;
  optional?: boolean;
  maxLength: number;
  hint?: string;
  onInput: (value: string) => void;
}

export function Textarea({
  label,
  value,
  placeholder,
  optional,
  maxLength,
  hint,
  onInput,
}: TextareaProps) {
  return (
    <FieldWrap label={label} optional={optional}>
      <textarea
        class="tl-textarea"
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        onInput={(e) =>
          onInput((e.currentTarget as HTMLTextAreaElement).value)
        }
      />
      <div class="tl-textarea__meta">
        {hint && <span class="tl-textarea__hint">{hint}</span>}
        <span class="tl-textarea__counter">
          {STRINGS.freitextCounter(value.length)}
        </span>
      </div>
    </FieldWrap>
  );
}

interface CheckboxProps {
  label: string;
  linkHref?: string;
  required?: boolean;
  checked: boolean;
  ariaDescribedBy?: string;
  onChange: (checked: boolean) => void;
}

export function Checkbox({
  label,
  linkHref,
  required,
  checked,
  ariaDescribedBy,
  onChange,
}: CheckboxProps) {
  const parsed = linkHref ? parseLinkMarker(label) : null;
  return (
    <label class="tl-checkbox">
      <input
        type="checkbox"
        checked={checked}
        aria-describedby={ariaDescribedBy}
        onChange={(e) =>
          onChange((e.currentTarget as HTMLInputElement).checked)
        }
      />
      <span>
        {parsed ? (
          <>
            {parsed.before}
            <a href={linkHref} target="_blank" rel="noopener noreferrer">
              {parsed.link}
            </a>
            {parsed.after}
          </>
        ) : (
          label
        )}
        {required && <span class="tl-field__required"> *</span>}
      </span>
    </label>
  );
}
